/**
 * /pourquoi — la page des problèmes que Noam résout.
 * WHY → WHAT CHANGES → HOW. Hors menu, accessible via les CTA de la Home.
 * Aucun chiffre inventé : mécanismes économiques vérifiables uniquement.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const FR = {
  seo_t: "Pourquoi Noam · Les problèmes que nous résolvons",
  seo_d: "Une organisation possède énormément d'informations. Elle ne dispose pas nécessairement d'une compréhension continue de ce qu'elle sait, de ce qu'elle ignore et de ce qui change. Noam résout ce problème.",
  ov: "Pourquoi Noam",
  h1: ["Une organisation possède", "énormément d'informations."],
  h1b: "Elle ne dispose pas nécessairement d'une compréhension continue de ce qu'elle sait, de ce qu'elle ignore, et de ce qui est en train de changer.",
  h1c: "C'est le problème supérieur que Noam résout. Tout le reste en découle.",
  s1_ov: "I · Les douleurs concrètes",
  s1_t: ["Onze situations que", "vous avez déjà vécues."],
  s1_lead: "Chaque douleur suit la même logique : le problème, sa conséquence économique ou opérationnelle, ce que Noam change, et le mécanisme par lequel la valeur se crée.",
  csq: "Conséquence", chg: "Ce que Noam change", mec: "Mécanisme",
  pains: [
    ["Les informations sont dispersées",
     "Entre personnes, documents, logiciels, processus et sources externes. Retrouver qui sait quoi prend des heures.",
     "Du temps de travail qualifié consommé à chercher au lieu de décider.",
     "Noam relie documents, registres, personnes et sources en un monde unique et interrogeable.",
     "Temps de recherche réduit"],
    ["Les décisions se prennent avec une vision partielle",
     "Personne ne voit l'ensemble : chaque service détient un fragment du contexte.",
     "Des paris involontaires, découverts après coup.",
     "Chaque opportunité reçoit une note, le commentaire franc de Noam, et les éléments concrets pour améliorer cette note. Chaque scénario est chiffré, sourcé et relié à ce que l'organisation sait réellement.",
     "Décisions notées, commentées, améliorables"],
    ["Les signaux faibles arrivent trop tard",
     "Risque, dérive, changement fournisseur, évolution réglementaire : visibles quand ils produisent déjà leurs effets.",
     "On gère la conséquence au lieu de traiter la cause.",
     "Le Radar croise le monde extérieur avec vos expositions réelles, chaque semaine.",
     "Risques vus plus tôt · pertes potentiellement évitées"],
    ["La connaissance dépend de quelques personnes",
     "Ce que sait l'entreprise vit dans des têtes. Un départ, et le contexte part avec.",
     "Fragilité structurelle et coûts de reconstruction invisibles.",
     "La connaissance est capturée dans le graphe : objets, relations, provenance, historique.",
     "Continuité indépendante des personnes"],
    ["Les raisonnements des décisions disparaissent",
     "Pourquoi a-t-on décidé cela ? Sur quelles hypothèses ? Quelques mois plus tard, personne ne sait plus.",
     "Impossible d'apprendre de ses propres décisions, ni de les défendre.",
     "Chaque décision conserve ses preuves, ses hypothèses, son verdict et son résultat réel.",
     "Mémoire décisionnelle auditable"],
    ["Les mêmes analyses sont refaites plusieurs fois",
     "La même recherche, la même due diligence, la même synthèse, refaites par des équipes différentes.",
     "Du travail déjà payé, payé une deuxième fois.",
     "Ce qui a été analysé une fois reste relié aux objets concernés et resservira.",
     "Travail déjà réalisé réutilisé"],
    ["Les processus dérivent silencieusement",
     "Un contrôle sauté, un circuit contourné, une exception devenue habitude.",
     "Les conséquences apparaissent longtemps après, au pire moment.",
     "Les exécutions sont reliées aux contrôles et aux risques : la dérive devient visible tôt.",
     "Dérives détectées avant leurs conséquences"],
    ["Risques, contrôles et opérations sont analysés séparément",
     "Trois registres, trois outils, trois lectures. Personne ne voit le lien.",
     "Des angles morts précisément là où les problèmes se propagent.",
     "Risques, contrôles, exécutions et plans d'action vivent dans le même monde relié.",
     "Vue GRC unifiée et vivante"],
    ["L'environnement externe n'est pas relié à l'interne",
     "La veille existe, mais elle ne sait pas ce qui compte pour vos dossiers.",
     "Des signaux pertinents noyés dans du bruit générique.",
     "Un signal n'a de valeur que rapporté à une exposition : Noam fait ce croisement automatiquement.",
     "Veille contextualisée par vos dossiers"],
    ["Le reporting exige de reconstruire le contexte à la main",
     "Chaque comité, chaque audit, chaque demande de bailleur déclenche une reconstruction manuelle.",
     "Des jours de travail pour prouver ce qui aurait dû être déjà prouvé.",
     "La chaîne preuve, décision, exécution, résultat existe déjà : le reporting en découle.",
     "Reporting dérivé, non reconstruit"],
    ["Les opportunités ne sont pas identifiées",
     "Les informations existent, mais personne ne les relie entre elles au bon moment.",
     "De la valeur qui passe, sans même être vue.",
     "Le graphe relie acteurs, actifs, signaux et besoins : les rapprochements deviennent visibles, et chaque opportunité identifiée est notée avec ses leviers d'amélioration.",
     "Opportunités identifiées et notées"],
  ],
  s2_ov: "II · La valeur, honnêtement",
  s2_t: ["Ce que ça vaut,", "sans ROI fictif."],
  s2_p1: "Nous ne publions pas de retour sur investissement théorique. Les gains passent par des mécanismes vérifiables : temps de recherche réduit, risques vus plus tôt, opportunités identifiées, décisions mieux contextualisées, travail déjà réalisé réutilisé, coûts ou pertes potentiellement évités.",
  s2_p2: "Lorsque l'usage réel le permettra, Noam mesurera lui-même la valeur créée :",
  s2_formula: "Valeur additionnelle captée + pertes évitées + coûts opérationnels économisés + amélioration de l'allocation du capital − coût de Noam et des actions engagées.",
  s2_p3: "Les résultats historiques contrôlés, quand ils existent, sont présentés comme benchmarks avec leur méthodologie et leurs limites. Jamais comme une promesse de performance future.",
  s3_ov: "III · Les trois questions",
  q3: ["Qu'est-ce que mon organisation sait ?", "Qu'est-ce qu'elle ne sait pas encore ?", "Qu'est-ce qui est en train de changer et mérite mon attention ?"],
  q3_close: "Noam est construit pour répondre à ces trois questions, en continu.",
  cta_platform: "Voir comment Noam est construit",
  cta_camp: "Le Noam Camp : la preuve en 2 heures",
  cta_access: "Initier une demande",
};

const EN = {
  seo_t: "Why Noam · The problems we solve",
  seo_d: "An organization holds enormous amounts of information. It does not necessarily hold a continuous understanding of what it knows, what it doesn't, and what is changing. Noam solves that problem.",
  ov: "Why Noam",
  h1: ["An organization holds", "enormous amounts of information."],
  h1b: "It does not necessarily hold a continuous understanding of what it knows, what it doesn't know, and what is changing right now.",
  h1c: "That is the higher-order problem Noam solves. Everything else follows.",
  s1_ov: "I · The concrete pains",
  s1_t: ["Eleven situations you", "have already lived through."],
  s1_lead: "Every pain follows the same logic: the problem, its economic or operational consequence, what Noam changes, and the mechanism through which value is created.",
  csq: "Consequence", chg: "What Noam changes", mec: "Mechanism",
  pains: [
    ["Information is scattered",
     "Across people, documents, software, processes and external sources. Finding out who knows what takes hours.",
     "Skilled working time consumed searching instead of deciding.",
     "Noam connects documents, registers, people and sources into one queryable world.",
     "Search time reduced"],
    ["Decisions are made on a partial view",
     "No one sees the whole picture: each department holds a fragment of the context.",
     "Involuntary bets, discovered after the fact.",
     "Every opportunity receives a score, Noam's candid commentary, and the concrete elements to improve that score. Every scenario is quantified, sourced and connected to what the organization actually knows.",
     "Decisions scored, commented, improvable"],
    ["Weak signals arrive too late",
     "A risk, a drift, a supplier change, a regulatory shift: visible once they already produce effects.",
     "You manage the consequence instead of treating the cause.",
     "The Radar crosses the outside world with your real exposures, every week.",
     "Risks seen earlier · losses potentially avoided"],
    ["Knowledge depends on a few people",
     "What the company knows lives in heads. One departure, and the context leaves with it.",
     "Structural fragility and invisible reconstruction costs.",
     "Knowledge is captured in the graph: objects, relationships, provenance, history.",
     "Continuity independent of individuals"],
    ["The reasoning behind decisions disappears",
     "Why did we decide that? On which assumptions? Months later, nobody remembers.",
     "Impossible to learn from your own decisions, or to defend them.",
     "Every decision keeps its evidence, its assumptions, its verdict and its actual outcome.",
     "Auditable decision memory"],
    ["The same analyses are redone several times",
     "The same research, the same due diligence, the same summary, redone by different teams.",
     "Work already paid for, paid for a second time.",
     "What has been analyzed once stays connected to the objects concerned and will serve again.",
     "Work already done, reused"],
    ["Processes drift silently",
     "A skipped control, a bypassed procedure, an exception that became a habit.",
     "Consequences surface much later, at the worst moment.",
     "Executions are linked to controls and risks: drift becomes visible early.",
     "Drift detected before its consequences"],
    ["Risks, controls and operations are analyzed separately",
     "Three registers, three tools, three readings. Nobody sees the link.",
     "Blind spots exactly where problems propagate.",
     "Risks, controls, executions and action plans live in the same connected world.",
     "Unified, living GRC view"],
    ["The external environment is not connected to the internal one",
     "Monitoring exists, but it doesn't know what matters to your files.",
     "Relevant signals drowned in generic noise.",
     "A signal only has value against an exposure: Noam makes that cross-reference automatically.",
     "Monitoring contextualized by your files"],
    ["Reporting requires rebuilding context by hand",
     "Every committee, every audit, every funder request triggers a manual reconstruction.",
     "Days of work to prove what should already be proven.",
     "The evidence, decision, execution, outcome chain already exists: reporting derives from it.",
     "Reporting derived, not rebuilt"],
    ["Opportunities go unidentified",
     "The information exists, but nobody connects it at the right moment.",
     "Value passing by, without even being seen.",
     "The graph connects actors, assets, signals and needs: the matches become visible, and every identified opportunity is scored with its improvement levers.",
     "Opportunities identified and scored"],
  ],
  s2_ov: "II · The value, honestly",
  s2_t: ["What it is worth,", "with no fictional ROI."],
  s2_p1: "We do not publish theoretical return on investment. Gains come through verifiable mechanisms: search time reduced, risks seen earlier, opportunities identified, better-contextualized decisions, work already done reused, costs or losses potentially avoided.",
  s2_p2: "When real usage allows it, Noam will measure the value created itself:",
  s2_formula: "Additional value captured + losses avoided + operational costs saved + improved capital allocation − the cost of Noam and of the actions undertaken.",
  s2_p3: "Controlled historical results, when they exist, are presented as benchmarks with their methodology and limits. Never as a promise of future performance.",
  s3_ov: "III · The three questions",
  q3: ["What does my organization know?", "What doesn't it know yet?", "What is changing and deserves my attention?"],
  q3_close: "Noam is built to answer these three questions, continuously.",
  cta_platform: "See how Noam is built",
  cta_camp: "The Noam Camp: proof in 2 hours",
  cta_access: "Initiate a request",
};

function SectionHead({ ov, t2, lead }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end mb-14">
      <div className="md:col-span-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{ov}</div>
        <h2 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight mt-4 leading-tight">
          {t2[0]}<br />{t2[1]}
        </h2>
      </div>
      {lead && (
        <div className="md:col-span-7 md:pl-10">
          <p className="text-sm text-zinc-400 max-w-md leading-relaxed">{lead}</p>
        </div>
      )}
    </div>
  );
}

export default function Pourquoi() {
  const { i18n } = useTranslation();
  const en = (i18n.language || "fr").startsWith("en");
  const L = en ? EN : FR;
  useSEO({ title: L.seo_t, description: L.seo_d, path: "/pourquoi" });
  return (
    <div className="relative overflow-hidden" data-testid="pourquoi-root">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <img src="/home/pourquoi_hero.jpg" alt="" className="noam-kenburns absolute inset-0 w-full h-full object-cover opacity-35 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E13] via-[#0A0E13]/75 to-[#0A0E13]/30 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 py-24 w-full">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#EF9F27] noam-fade-in">{L.ov}</div>
          <h1 className="noam-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] mt-6 noam-fade-in" style={{ animationDelay: "0.12s" }} data-testid="pourquoi-h1">
            {L.h1[0]}<br />{L.h1[1]}
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed noam-fade-in" style={{ animationDelay: "0.25s" }}>{L.h1b}</p>
          <p className="mt-4 text-sm text-[#43C6D8] max-w-2xl leading-relaxed noam-fade-in" style={{ animationDelay: "0.35s" }}>{L.h1c}</p>
        </div>
      </section>

      {/* I · DOULEURS */}
      <section className="relative border-t border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <SectionHead ov={L.s1_ov} t2={L.s1_t} lead={L.s1_lead} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5" data-testid="pourquoi-pains">
            {L.pains.map((p, i) => (
              <div key={i} className="bg-[#0A0E13] px-7 py-8 hover:bg-white/[0.02] transition-colors" data-testid={`pourquoi-pain-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <h3 className="noam-display text-lg font-bold tracking-tight text-white leading-snug">{p[0]}</h3>
                  <span className="text-[10px] font-mono text-zinc-600 shrink-0 mt-1">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <p className="text-[13px] text-zinc-400 mt-3 leading-relaxed">{p[1]}</p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-600">{L.csq}</div>
                    <p className="text-[12px] text-zinc-500 mt-1 italic leading-relaxed">{p[2]}</p>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-[#43C6D8]/70">{L.chg}</div>
                    <p className="text-[12px] text-zinc-300 mt-1 leading-relaxed">{p[3]}</p>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-2">
                  <span className="text-[9px] uppercase tracking-[0.25em] text-zinc-600">{L.mec}</span>
                  <span className="text-[11px] text-[#EF9F27]">{p[4]}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* II · LA VALEUR HONNÊTEMENT */}
      <section className="relative border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <SectionHead ov={L.s2_ov} t2={L.s2_t} />
          <div className="max-w-3xl">
            <p className="text-sm text-zinc-300 leading-relaxed" data-testid="pourquoi-honest-p1">{L.s2_p1}</p>
            <p className="text-sm text-zinc-400 leading-relaxed mt-6">{L.s2_p2}</p>
            <div className="mt-4 border-l-2 border-[#43C6D8]/50 bg-white/[0.02] px-6 py-5" data-testid="pourquoi-formula">
              <p className="text-[15px] text-white leading-relaxed noam-display font-semibold">{L.s2_formula}</p>
            </div>
            <p className="text-[13px] text-zinc-500 leading-relaxed mt-6">{L.s2_p3}</p>
          </div>
        </div>
      </section>

      {/* III · LES TROIS QUESTIONS + CTA */}
      <section className="relative border-t border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-10">{L.s3_ov}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pourquoi-questions">
            {L.q3.map((q, i) => (
              <p key={i} className="noam-display text-xl font-bold tracking-tight text-white leading-snug">
                <span className="text-[#43C6D8] mr-2 font-mono text-sm">Q{i + 1}</span>{q}
              </p>
            ))}
          </div>
          <p className="mt-8 text-sm text-zinc-400">{L.q3_close}</p>
          <div className="mt-10">
            <Link to="/platform" className="inline-flex items-center gap-2 text-xs text-[#43C6D8] hover:text-white transition-colors group" data-testid="pourquoi-cta-platform">
              {L.cta_platform} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            <Link to="/camp"
              className="group bg-[#0A0E13] px-8 sm:px-12 py-14 sm:py-16 transition-colors relative overflow-hidden"
              data-testid="pourquoi-cta-camp">
              <img src="/home/news_camp.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-35 group-hover:scale-[1.03] transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E13] via-[#0A0E13]/70 to-transparent" />
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#EF9F27]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <h3 className="noam-display text-2xl sm:text-3xl font-bold tracking-tight text-white group-hover:text-[#EF9F27] transition-colors flex items-center gap-4">
                  {L.cta_camp} <ArrowRight className="w-6 h-6 group-hover:translate-x-1.5 transition-transform" />
                </h3>
              </div>
            </Link>
            <Link to="/request-access"
              className="group bg-[#0A0E13] px-8 sm:px-12 py-14 sm:py-16 transition-colors relative overflow-hidden"
              data-testid="pourquoi-cta-access">
              <img src="/home/cta_demo.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-35 group-hover:scale-[1.03] transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E13] via-[#0A0E13]/70 to-transparent" />
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#43C6D8]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <h3 className="noam-display text-2xl sm:text-3xl font-bold tracking-tight text-white group-hover:text-[#43C6D8] transition-colors flex items-center gap-4">
                  {L.cta_access} <ArrowRight className="w-6 h-6 group-hover:translate-x-1.5 transition-transform" />
                </h3>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
